<template>
  <div
    v-if="isSearchInputOnly"
    class="data-grid__search-only"
    data-testid="search-header-only"
  >
    <form-input
      v-model="searchValue"
      :label="searchPlaceholder"
      type="search"
      :name="searchName"
      is-inline
      hide-label
      class="data-grid__search-bar"
      data-testid="search-header-input"
      @keyup.enter="submitSearch"
    />
  </div>
  <div
    v-else
    class="d-flex justify-content-between data-grid__header-inner"
  >
    <div
      class="d-flex data-grid__header-search"
      data-testid="search-header-buttons"
    >
      <form-input
        v-model="searchValue"
        :label="searchPlaceholder"
        type="search"
        :name="searchName"
        is-inline
        hide-label
        class="data-grid__search-bar"
        data-testid="search-header-input"
      />
      <f-button 
        text="Reset" 
        type="secondary" 
        icon="close" 
        data-testid="search-reset-btn" 
        @click="resetSearch" 
      />
      <f-button 
        text="Search"  
        type="primary-alt" 
        icon="search" 
        data-testid="search-submit-btn" 
        @click="submitSearch" 
      />
    </div>
    <f-button 
      text="Reset table"  
      type="inline" 
      class="btn--tertiary" 
      data-testid="search-clear-btn" 
      @click="clearSort" 
    />
  </div>
</template>

<script>
import { defineComponent, ref, watch, onMounted } from "vue";
export default defineComponent({
  name: "DataGridSearchHeader",
  props:{
    searchPlaceholder: {
      type: String,
      default: ""
    },
    searchName:{
      type: String,
      required: true
    },
    endpointSearchParam:{
      type: String,
      default: ""
    },
    isSearchInputOnly:{
      type: Boolean,
      default: false
    },
    persistedSearchValue:{
      type: String,
      default: ""
    }
  },
  emits: ["reset-search", "submit-search", "clear-sort", "update-search-value"],
  setup(props, {emit}) {
    const searchValue = ref(props.persistedSearchValue || props.endpointSearchParam);
    const searchDebounceTimer = ref(null);

    // Watch for search value changes and emit to parent
    // Update search value immediately but debounce the actual search submission
    watch(searchValue, (newValue) => {
      // Emit immediate update for UI state synchronization
      emit("update-search-value", newValue);

      // Debounce the actual search API call (300ms)
      if (searchDebounceTimer.value) {
        clearTimeout(searchDebounceTimer.value);
      }
      searchDebounceTimer.value = setTimeout(() => {
        emit("submit-search", newValue);
      }, 300);
    });

    // Trigger search on mount if there's a persisted value
    // This ensures search executes when switching between current/archived
    onMounted(() => {
      if (searchValue.value) {
        emit("submit-search", searchValue.value);
      }
    });

    const resetSearch = () => {
      searchValue.value = "";
      emit("reset-search");
    };

    const submitSearch = () => {
      emit("submit-search", searchValue.value);
    };

    const clearSort = () =>{
      searchValue.value = "";
      emit("clear-sort");
    };

    return {
      resetSearch,
      submitSearch,
      clearSort,
      searchValue
    };
  },
});
</script>

<style lang="scss" scoped>
  .data-grid__search-only{
    width: 100%;
    max-width: 500px;

    :deep(.data-grid__search-bar){
      width: 100%
    }
  }
  .data-grid__header-inner{
    flex-wrap: wrap;
  }

  .data-grid__header-search{
    flex-wrap: wrap;
    flex: 1 1 100%;
    justify-content: flex-start;

    @media(min-width: 768px){
      flex: 0 0 auto;
      flex-wrap: nowrap;
    }

    .btn{
      @media(min-width: 768px){
        margin-left: 16px;
      }
    }

    .btn:last-of-type{
      margin-left: 16px;
    }
  }

  :deep(.field--inline){
    width:100%;
    margin-bottom: 8px;

    @media(min-width: 768px){
      margin-bottom: 0;
    }
  }

  :deep(.data-grid__search-bar){
    min-width: 150px;
  }

  .btn--tertiary{
    margin-top:16px;

    @media(min-width: 930px){
      margin-top: 0;
    }
  }
</style>
